/**
 * The water material's uniforms for the pale band along the shores (STYLE §7, shoreBand.glsl): the
 * shore grid of shoreGrid.ts in three float textures the shader reads texel by texel, and the band's
 * fits. They start empty, laying no band, until the world's water sets the site's shores.
 */
import {
  ClampToEdgeWrapping,
  DataTexture,
  FloatType,
  NearestFilter,
  RedFormat,
  RGBAFormat,
  Vector4,
  type IUniform,
} from 'three';
import { SHORE_BAND, type ShoreBandFit } from '../shoreBand';
import { EMPTY_SHORE_GRID, SHORE_GRID, type ShoreGrid } from '../shoreGrid';

export interface ShoreUniforms {
  uShoreCells: IUniform<DataTexture>;
  uShoreIndex: IUniform<DataTexture>;
  uShoreSegments: IUniform<DataTexture>;
  /** The grid's origin x and y, its cell size and the band's reach, in layout metres. */
  uShoreGrid: IUniform<Vector4>;
  /** The shift in a* and b* at the shore, and the width over which it falls by e. */
  uShoreBandOpen: IUniform<Vector4>;
  uShoreBandChannel: IUniform<Vector4>;
}

type Channels = 1 | 4;

function floatTexture(
  data: Float32Array,
  width: number,
  height: number,
  channels: Channels
): DataTexture {
  const texture = new DataTexture(
    data,
    width,
    height,
    channels === 1 ? RedFormat : RGBAFormat,
    FloatType
  );
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;
  texture.generateMipmaps = false;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

/* Texel n at (n mod width, n div width), the last row padded, as shoreTexel reads it. */
function rowTexture(data: Float32Array, channels: Channels): DataTexture {
  const width = SHORE_GRID.textureWidth;
  const height = Math.max(1, Math.ceil(data.length / channels / width));
  const rows = new Float32Array(width * height * channels);
  rows.set(data);
  return floatTexture(rows, width, height, channels);
}

function bandVector({ shift, widthM }: ShoreBandFit): Vector4 {
  return new Vector4(shift[0], shift[1], widthM, 0);
}

function gridTextures(grid: ShoreGrid) {
  return {
    cells: floatTexture(grid.cells, grid.size[0], grid.size[1], 1),
    index: rowTexture(grid.index, 1),
    segments: rowTexture(grid.segments, 4),
  };
}

export function createShoreUniforms(grid: ShoreGrid = EMPTY_SHORE_GRID): ShoreUniforms {
  const { cells, index, segments } = gridTextures(grid);
  return {
    uShoreCells: { value: cells },
    uShoreIndex: { value: index },
    uShoreSegments: { value: segments },
    uShoreGrid: { value: new Vector4(grid.origin[0], grid.origin[1], grid.cellM, grid.reachM) },
    uShoreBandOpen: { value: bandVector(SHORE_BAND.open) },
    uShoreBandChannel: { value: bandVector(SHORE_BAND.channel) },
  };
}

export function disposeShoreTextures(uniforms: ShoreUniforms): void {
  uniforms.uShoreCells.value.dispose();
  uniforms.uShoreIndex.value.dispose();
  uniforms.uShoreSegments.value.dispose();
}

/**
 * Lays a grid into the uniforms in place, since the water material holds them, and frees the
 * textures it replaces.
 */
export function setShoreGrid(uniforms: ShoreUniforms, grid: ShoreGrid): void {
  const { cells, index, segments } = gridTextures(grid);
  disposeShoreTextures(uniforms);
  uniforms.uShoreCells.value = cells;
  uniforms.uShoreIndex.value = index;
  uniforms.uShoreSegments.value = segments;
  uniforms.uShoreGrid.value.set(grid.origin[0], grid.origin[1], grid.cellM, grid.reachM);
}
